import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, Package, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerTrigger, DrawerHeader, DrawerTitle, DrawerFooter } from '@/components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { fetchProducts } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';
import { ProductGridSkeleton } from '@/components/ProductCardSkeleton';
import { Footer } from '@/components/Footer';
import { classNames } from '@/lib/format';

const CATEGORIES = [
  { id: 'all', label: 'All packs' },
  { id: 'chocopops', label: 'ChocoPops' },
  { id: 'chocosnaps', label: 'ChocoSnaps' },
  { id: 'chocodigits', label: 'Choco Digits' },
];

const PACK_TYPES = [
  { id: 'all', label: 'All' },
  { id: 'assorted', label: 'Assorted Pack' },
  { id: 'mono', label: 'Mono Pack' },
];

const ShopPage = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || 'all';
  const sort = params.get('sort') || 'featured';
  const showAllPieces = params.get('view') === 'pieces';
  const packType = params.get('pack_type') || 'all'; // all | assorted | mono
  const minP = Number(params.get('min') || 0);
  const maxP = Number(params.get('max') || 5000);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([minP, maxP]);

  useEffect(() => {
    setLoading(true);
    fetchProducts({ category, sort })
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category, sort]);

  const visible = useMemo(() => {
    return products.filter((p) => {
      // By default show only packs (boxes). Individual pieces hidden unless ?view=pieces
      if (!showAllPieces && !p.is_pack) return false;
      if (showAllPieces && p.is_pack) return false;
      // Pack-type filter (only meaningful when viewing packs)
      if (!showAllPieces && packType === 'assorted' && p.is_mono) return false;
      if (!showAllPieces && packType === 'mono' && !p.is_mono) return false;
      if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
      return true;
    });
  }, [products, priceRange, showAllPieces, packType]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === null || value === undefined || value === '' || value === 'all') next.delete(key);
    else next.set(key, String(value));
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="shop-page">
      <section className="bb-block-yellow">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 text-[var(--bb-darkblue)]">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-darkblue)] text-white text-xs font-nunito-black uppercase tracking-wider">
            <Package className="w-4 h-4" /> Sold by the box
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95] text-[var(--bb-darkblue)]">Shop our packs</h1>
          <p className="text-sm md:text-base text-[var(--bb-darkblue)]/90 mt-3 font-nunito-medium max-w-xl">
            Three signature boxes — 3D Choco Pops, Choco Snaps, Choco Digits. Each box is composed, customised and made-to-order.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs font-nunito-bold">
            <Star className="w-4 h-4 fill-[var(--bb-darkblue)]" />
            <span>{visible.length} {showAllPieces ? 'pieces' : 'packs'} · ships to select locations</span>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6">
        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-1 px-1 pb-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => updateParam('category', c.id === 'all' ? null : c.id)}
              className={classNames(
                'shrink-0 h-9 px-4 rounded-full text-sm font-nunito-black border-2 transition-colors',
                category === c.id
                  ? 'bg-[var(--bb-cyan)] text-white border-transparent'
                  : 'bg-white border-[var(--bb-darkblue)]/10 text-[var(--bb-darkblue)] hover:bg-[var(--bb-cyan-soft)]',
              )}
              data-testid={`shop-cat-${c.id}`}
            >
              {c.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateParam('view', showAllPieces ? null : 'pieces')}
              className={classNames(
                'shrink-0 h-9 px-3 rounded-full text-xs font-nunito-black border-2 transition-colors',
                showAllPieces ? 'bg-[var(--bb-darkblue)] text-white border-transparent' : 'bg-white border-[var(--bb-darkblue)]/10 text-[var(--bb-darkblue)] hover:bg-[var(--bb-yellow-soft)]',
              )}
              data-testid="shop-toggle-view"
            >
              {showAllPieces ? 'Show packs' : 'Browse individual pieces'}
            </button>
          </div>
        </div>

        {/* Pack-type pills (Assorted vs Mono). Hidden when viewing individual pieces. */}
        {!showAllPieces && (
          <div className="flex gap-2 items-center mt-2 -mx-1 px-1 pb-1" data-testid="shop-pack-type-row">
            <span className="text-[11px] font-nunito-black uppercase tracking-wider text-[var(--bb-darkgrey)] mr-1">Pack type</span>
            {PACK_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => updateParam('pack_type', t.id === 'all' ? null : t.id)}
                className={classNames(
                  'shrink-0 h-8 px-3.5 rounded-full text-xs font-nunito-black border-2 transition-colors',
                  packType === t.id
                    ? 'bg-[var(--bb-pink)] text-white border-transparent'
                    : 'bg-white border-[var(--bb-darkblue)]/10 text-[var(--bb-darkblue)] hover:bg-[var(--bb-pink-soft)]',
                )}
                data-testid={`shop-pack-type-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Sort & filters bar */}
        <div className="flex items-center gap-2 mt-3">
          <Drawer>
            <DrawerTrigger asChild>
              <Button variant="outline" className="h-10 rounded-xl gap-2 border-2 font-nunito-bold" data-testid="open-filters-button">
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader><DrawerTitle>Filter products</DrawerTitle></DrawerHeader>
              <div className="px-4 pb-3 space-y-4">
                <div>
                  <div className="text-sm font-nunito-black mb-2">Price range</div>
                  <Slider min={0} max={5000} step={50} value={priceRange} onValueChange={setPriceRange} className="my-2" data-testid="price-slider" />
                  <div className="flex justify-between text-xs text-[var(--bb-darkgrey)] font-nunito-medium">
                    <span>₹{priceRange[0]}</span>
                    <span>₹{priceRange[1]}</span>
                  </div>
                </div>
              </div>
              <DrawerFooter className="flex-row gap-2">
                <Button variant="outline" className="flex-1 h-11 rounded-xl border-2" onClick={() => setPriceRange([0, 5000])}>Reset</Button>
                <Button className="flex-1 h-11 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black" data-testid="apply-filters-button">Show {visible.length} results</Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          <Select value={sort} onValueChange={(v) => updateParam('sort', v === 'featured' ? null : v)}>
            <SelectTrigger className="w-[160px] h-10 rounded-xl ml-auto border-2 font-nunito-bold" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Featured</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Active filter chips */}
        {(category !== 'all' || packType !== 'all') && (
          <div className="flex flex-wrap gap-2 mt-3">
            {category !== 'all' && (
              <button onClick={() => updateParam('category', null)} className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--bb-cyan-soft)] text-xs font-nunito-black text-[var(--bb-darkblue)]" data-testid="active-chip-category">
                {CATEGORIES.find((c) => c.id === category)?.label} <X className="w-3 h-3" />
              </button>
            )}
            {packType !== 'all' && !showAllPieces && (
              <button onClick={() => updateParam('pack_type', null)} className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-[var(--bb-pink-soft)] text-xs font-nunito-black text-[var(--bb-darkblue)]" data-testid="active-chip-pack-type">
                {PACK_TYPES.find((t) => t.id === packType)?.label} <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Grid */}
        <div className="mt-4" data-testid="shop-product-grid">
          {loading ? (
            <ProductGridSkeleton count={6} />
          ) : visible.length === 0 ? (
            <div className="py-16 text-center" data-testid="empty-state">
              <div className="mx-auto w-12 h-12 rounded-full bg-[var(--bb-cyan-soft)] flex items-center justify-center">
                <SlidersHorizontal className="w-5 h-5 text-[var(--bb-cyan)]" />
              </div>
              <h3 className="font-display text-xl text-[var(--bb-darkblue)] mt-2">Nothing matches those filters</h3>
              <p className="text-sm text-[var(--bb-darkgrey)] font-nunito-medium mt-1">Try clearing them or browse all packs.</p>
              <Button asChild className="mt-4 h-11 rounded-xl bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white font-nunito-black">
                <Link to="/shop">Browse all packs</Link>
              </Button>
            </div>
          ) : (
            <div className={classNames(
              'grid gap-3',
              showAllPieces ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
            )}>
              {visible.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShopPage;
